// How old the figures on screen are, said once: in the shell's footer under the
// build, on every page (ZENITH.md §18, #1315). A page's reader reports each age
// it holds — the figures a display read answered with (`data_as_of`), Zenith's
// copy of a cell's device listing the device lists come from (`listing_as_of`),
// the moment a topology's switches were read — and the footer shows the oldest
// as a muted dot and a short relative age, with the sentence that says what the
// age means as the element's accessible name and `title`, never as visible
// copy. Until this each page said it in words of its own, in a place of its
// own: the Overview's head, the Wireless and Clients heads, the device lists'
// count line, the topology's bar.
//
// It requests nothing. The page's own `useQuery` is unchanged, and what travels
// here is a value the page already holds. It is not a cache of server state —
// that is #198's question, ruled by ADR-0419 `1d`, and #1862 holds its re-read —
// but one shared reader for one shape of page-to-shell state, in `estate.tsx`'s
// pattern: a context the shell owns, a hook a page's reader calls, and an entry
// dropped when that reader unmounts, so a route change clears it because the
// page unmounts.
//
// **Decided on the item, so this file does not decide them again** (#1315):
//
// - A page holding figures of more than one age shows the oldest, which is how
//   `data_as_of` is already defined — the oldest controller reading the answer
//   was composed from (api/openapi.yaml) — and how a customer's device list
//   already reports its oldest listing. The long form names each age.
// - A page with no display read shows nothing, not "live". An absent age is
//   not a value to render (conventions/dashboards-as-code.md), and "live" would
//   be a claim the shell cannot make: Users or Orgs read no cell at all. (A
//   device's own read carried no age field until #1802; it is a display read
//   now, and its `data_as_of` is reported like any other.)
// - While a refresh is in flight the age keeps ageing: the figures on screen
//   stay until the answer lands (`api/hooks.ts`), the page keeps reporting
//   them, and the compact form says nothing about the request. After a failed
//   refresh the figures that stayed keep ageing here; that the refresh failed
//   is the page's sentence (#1310).
// - The dot is one colour at every age. A colour that changed with age would
//   encode a bound nobody ruled (ZENITH.md §29), and the dot carries nothing
//   the text does not (theme.css: "dot plus word. Never colour alone").
// - Not an `aria-live` region: a relative age re-said every few seconds in a
//   live region would announce on every tick. The long form is read on hover,
//   and by a screen reader as the element's name.
// - On a phone it goes where the build goes, the account menu (#1132), because
//   the ruling places it under the running version; `BuildLabel` renders it,
//   so the two places are one line.
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState, type ReactNode } from "react";

/** The noun an age belongs to: the figures a display read answered with,
 * Zenith's copy of a cell's device listing, or the switches a topology read. */
export type AgeOf = "figures" | "listing" | "switches";

/** One age a page holds. */
export interface Age {
  what: AgeOf;
  /** When the oldest reading it describes was taken, as the server stated it
   * — or, for the switches, when their last read came back. */
  at: string;
  /** When the answer itself was composed, where the answer carries that (the
   * dashboard's `generated_at`). Equal to `at` when the answer read nothing
   * from a cell and its figures are exactly as old as itself. */
  composedAt?: string;
}

/** The ages a page reports for one stamp, or none where the answer carried no
 * stamp — which is not a zero (conventions/dashboards-as-code.md): nothing is
 * reported, and the footer shows nothing. */
export function ageOf(what: AgeOf, at: string | undefined, composedAt?: string): Age[] {
  if (!at) return [];
  return [composedAt !== undefined ? { what, at, composedAt } : { what, at }];
}

interface Reports {
  report(id: string, ages: Age[]): void;
  drop(id: string): void;
}

// Two contexts rather than one: a page's reader needs the two functions, which
// never change, and only the footer needs the list, which changes on every
// report. One context would re-render every reporting page whenever any page
// reported.
const ReportCtx = createContext<Reports | undefined>(undefined);
const AgesCtx = createContext<readonly Age[] | undefined>(undefined);

export function FreshnessProvider({ children }: { children: ReactNode }) {
  const [reported, setReported] = useState<ReadonlyMap<string, Age[]>>(() => new Map());
  const report = useCallback((id: string, ages: Age[]) => setReported((m) => new Map(m).set(id, ages)), []);
  const drop = useCallback((id: string) => setReported((m) => {
    if (!m.has(id)) return m;
    const next = new Map(m);
    next.delete(id);
    return next;
  }), []);
  const reports = useMemo<Reports>(() => ({ report, drop }), [report, drop]);
  const ages = useMemo(() => [...reported.values()].flat(), [reported]);
  return <ReportCtx.Provider value={reports}><AgesCtx.Provider value={ages}>{children}</AgesCtx.Provider></ReportCtx.Provider>;
}

/** Says how old what this reader rendered is. Called by the page's reader with
 * the ages it holds — `ageOf(...)`, or several spread together — on every
 * render; reported when they change, and dropped when the reader unmounts. */
export function useReportedAge(ages: readonly Age[]): void {
  const r = useContext(ReportCtx);
  if (!r) throw new Error("useReportedAge outside FreshnessProvider");
  const id = useId();
  // Compared by value: a page composes its ages anew on every render, and an
  // effect keyed on the array would report the same thing each time.
  const key = JSON.stringify(ages);
  const { report, drop } = r;
  useEffect(() => { report(id, JSON.parse(key) as Age[]); }, [report, id, key]);
  useEffect(() => () => drop(id), [drop, id]);
}

/** How often the compact age is re-said. Not a §29 cadence: it reads no server
 * — the stamp is already in hand and only the clock moves — which is the same
 * argument `Version.tsx` makes for RECHECK_INTERVAL_MS. Five seconds is the
 * tick the topology's "Switches read" line ran on before it moved here. */
export const AGE_TICK_MS = 5_000;

/** The time now, again every `ms`, for a sentence that says how long ago. Here
 * from `devices/Topology.tsx`, whose "Switches read" line it drove. */
export function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(t); }, [ms]);
  return now;
}

/** An age in its largest whole unit — `40 s`, `2 min`, `27 h` — for the
 * compact form. Hours past a day stay hours, so a day and a half never reads
 * as a day; the long form carries the exact stamp. */
export function ageWords(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  return `${Math.floor(s / 3600)} h`;
}

const when = (iso: string) => new Date(iso).toLocaleString();

/** One clause of the long form per age the page reported. */
function clauseOf(a: Age): string {
  switch (a.what) {
    case "figures":
      // Restates ADR-0232 decision 4 and ADR-0228 decision 2 (`2b`), ADR-0295 — a display read past the read
      // cache's expiry is answered from the readings already held while one newer reading is fetched,
      // within the stale bound, so the figures are as old as data_as_of and not the time of the read;
      // and the answer's own time (`generated_at`) is not the figures' age, so it is said apart, where
      // the answer carries it and it differs.
      if (a.composedAt !== undefined && a.composedAt === a.at) {
        return `Figures composed at ${when(a.at)}, from what was held here then; nothing was read from a controller for them.`;
      }
      return `Figures from ${when(a.at)}: the oldest reading they were composed from — a controller reading held here, or the copy of a controller's device listing kept here. A refresh can show readings already held while newer ones are fetched, so the figures are as old as that reading and not as the refresh${a.composedAt !== undefined ? `; the answer itself was composed at ${when(a.composedAt)}` : ""}.`;
    case "listing":
      // Restates ADR-0234 decisions 1 (`1c`), 3 (`3c`) and 6 (`6a`), ZENITH.md §9.5, ADR-0295 — a list's
      // online, last contact, firmware, model and address come from Zenith's copy of the cell's
      // listing, which the standing sweep refreshes on its own tick, and the copy's age is shown.
      // Online is still judged against the clock at the time of the answer, from the copied session
      // and last contact, so a device whose last contact has aged past the bound reads offline however
      // old the copy is; there is no second bound, and an old copy is an old copy. The stamp is the
      // copy's and never a device's clock, and a device's own page reads the cell live. The bound is
      // §29's, so the copy says one exists and never what it is.
      return `Device listing from ${when(a.at)}: the copy of the controller's device listing kept here. The list's online, last contact, firmware, model and address come from that copy, which is refreshed on its own schedule. Whether a device is online is judged now, from the copied session and last contact, so a device that has not been heard from within the time allowed reads offline however old the copy is. A device's own page reads the controller live.`;
    case "switches":
      return `Switches read at ${when(a.at)}: what each switch reported then.`;
  }
}

/** The sentence the compact age stands for: how old the figures are and when
 * the oldest reading was taken, each age the page reported — oldest first,
 * each distinct stamp once — and whose clock it is. The element's accessible
 * name and its `title`, never visible copy. */
export function longForm(ages: readonly Age[], now: number): string {
  const sorted = [...ages].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const oldest = sorted[0]!;
  const seen = new Set<string>();
  const clauses = sorted
    .filter((a) => { const k = `${a.what}@${a.at}@${a.composedAt ?? ""}`; if (seen.has(k)) return false; seen.add(k); return true; })
    .map(clauseOf);
  // Restates ADR-0152 decision 1 (as amended by ADR-0222) and ADR-0097, ADR-0295 — a report the cell holds is
  // not the air now: the access point pushed it on its own schedule, so an age here is when Zenith
  // read or copied and never the device's own clock, whose `reported_at` is when the access point
  // wrote the report and is said beside it on the pages that list reports.
  return [
    `The figures on this page are ${ageWords(now - Date.parse(oldest.at))} old: the oldest reading they were composed from was taken at ${when(oldest.at)}.`,
    ...clauses,
    "Each is a reading or copy taken here and never a device's own clock; a report's time is when the access point wrote it, and is said beside the report.",
  ].join(" ");
}

/** The indicator under the build: a dot and the compact age of the oldest
 * reading on this page, the long form its name and its title. Nothing where no
 * page has reported. */
export function FiguresAge() {
  const ages = useContext(AgesCtx);
  if (!ages) throw new Error("FiguresAge outside FreshnessProvider");
  const now = useNow(AGE_TICK_MS);
  if (ages.length === 0) return null;
  const oldest = ages.reduce((a, b) => (Date.parse(b.at) < Date.parse(a.at) ? b : a));
  const long = longForm(ages, now);
  return (
    <time className="build-age" dateTime={oldest.at} aria-label={long} title={long}>
      <span className="dot" aria-hidden="true" />{ageWords(now - Date.parse(oldest.at))}
    </time>
  );
}
